import React, { useEffect, useMemo, useState } from 'react';
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import { darken } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';

import { BASEURL, PRIMARYCOLOR, COMPANY } from '../../../Constants';
import CoverPage from './coverPage';
import DisclaimerPage from './disclaimerPage';
import MethodologySection from './methodologyPage';
import ExecutiveReportAPIPentest from './executiveReport';
import WhiteLabelForm from '../../../components/whiteLabelForm';
import Nav from '../nav';

import { buildApiReportPdfData, SEVERITY_COLORS } from './apiReportPdfHelpers';

import './ReportsPage.css';

const DEFAULT_PRIMARY = PRIMARYCOLOR || '#005CC5';
const DEFAULT_COMPANY = COMPANY || 'StealthNet AI';
const DEFAULT_CLIENT = '';

const SeverityChip = ({ label }) => (
  <Chip
    label={label}
    sx={{
      mb: 1,
      px: 2,
      fontWeight: 700,
      backgroundColor: SEVERITY_COLORS[label] || '#64748b',
      color: '#fff',
    }}
  />
);

const TableOfContents = ({ items = [] }) => (
  <Box sx={{ p: '0.8in 0.9in' }} id="table-of-contents">
    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
      Table of Contents
    </Typography>

    <Box className="toc-wrapper">
      {items.map((item) => (
        <Box
          key={item.id}
          component="a"
          href={`#${item.id}`}
          className={`toc-row ${item.level === 2 ? 'toc-sub' : ''}`}
        >
          <span className="toc-title">{item.title}</span>
          <span className="toc-dots" />
        </Box>
      ))}
    </Box>
  </Box>
);

const downloadBlob = (blob, filename) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

const ReportsPageAPIPentest = () => {
  const navigate = useNavigate();
  const { pentest_id } = useParams();

  const [stats, setStats] = useState(null);
  const [exploits, setExploits] = useState([]);
  const [executiveSummary, setExecutiveSummary] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY);
  const [companyName, setCompanyName] = useState(DEFAULT_COMPANY);
  const [clientName, setClientName] = useState(DEFAULT_CLIENT);

  const applyWhiteLabel = ({ primary, company, client }) => {
    if (primary !== undefined) setPrimaryColor(primary);
    if (company !== undefined) setCompanyName(company);
    if (client !== undefined) setClientName(client);
  };

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };

    Promise.all([
      fetch(`${BASEURL}/pentests/api/stats?pentest_id=${pentest_id}`, { headers }).then((response) => response.json()),
      fetch(`${BASEURL}/pentests/api/exploits?pentest_id=${pentest_id}`, { headers }).then((response) => response.json()),
      fetch(`${BASEURL}/pentest/report/executive?pentest_id=${pentest_id}`, { headers }).then((response) => response.json()),
    ])
      .then(([statsRes, exploitsRes, summaryRes]) => {
        if ([statsRes, exploitsRes, summaryRes].some((result) => result?.message === 'Invalid token')) {
          navigate('/');
          return;
        }

        setStats(statsRes?.stats || statsRes || null);
        setExploits(exploitsRes?.exploits || []);
        setExecutiveSummary(summaryRes?.report || '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [navigate, pentest_id]);

  const report = useMemo(() => {
    if (!stats) return null;

    return buildApiReportPdfData({
      pentestId: pentest_id,
      primaryColor,
      companyName,
      clientName,
      stats,
      exploits,
      executiveSummary,
    });
  }, [clientName, companyName, executiveSummary, exploits, pentest_id, primaryColor, stats]);

  const handleDownloadPDF = async () => {
    if (!report) return;

    setDownloading(true);
    try {
      const [{ pdf }, { default: ApiReportPdfDocument }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('./apiReportPdf'),
      ]);

      const blob = await pdf(<ApiReportPdfDocument report={report} />).toBlob();
      downloadBlob(blob, `api_${pentest_id}_report.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  if (loading || !report) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <Nav />
        <Button
          variant="contained"
          onClick={handleDownloadPDF}
          sx={{
            position: 'absolute',
            top: 8,
            right: 16,
            backgroundColor: DEFAULT_PRIMARY,
            '&:hover': {
              backgroundColor: darken(DEFAULT_PRIMARY, 0.12),
            },
          }}
        >
          Download PDF
        </Button>
      </Box>

      <Backdrop open={downloading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Card className="report-config-card" sx={{ width: '100%', maxWidth: '8.5in', mb: 2, boxShadow: 'none', border: '1px solid rgba(0,0,0,0.08)' }}>
          <WhiteLabelForm
            defaultPrimary={DEFAULT_PRIMARY}
            defaultCompany={DEFAULT_COMPANY}
            defaultClient={DEFAULT_CLIENT}
            onApply={applyWhiteLabel}
            showCompany
            showClient
            title="White Label"
          />
        </Card>
      </Box>

      <Box className="report-platform-shell" sx={{ p: 2, '& > [aria-label^="pdf-page-"]': { mb: 2.5 } }}>
        <Box className="pdf-page cover-page" sx={{ display: 'flex', justifyContent: 'center' }} aria-label="pdf-page-0" data-report-section="cover">
          <Card
            className="report-page-card report-page-card--cover"
            sx={{
              width: '100%',
              maxWidth: '8.5in',
              height: '10.8in',
              m: 0,
              p: 0,
              border: 0,
              borderRadius: 0,
              boxShadow: 'none',
            }}
          >
            <CoverPage primary={primaryColor} company={companyName} clientName={report.clientName} reportDate={report.reportDate} />
          </Card>
        </Box>

        <Box className="pdf-page" sx={{ display: 'flex', justifyContent: 'center' }} aria-label="pdf-page-1" data-report-section="disclaimer">
          <Card className="report-page-card report-page-card--standard" sx={{ width: '100%', maxWidth: '8.5in' }}>
            <DisclaimerPage primary={primaryColor} company={companyName} />
          </Card>
        </Box>

        <Box className="pdf-page" sx={{ display: 'flex', justifyContent: 'center' }} aria-label="pdf-page-2" data-report-section="toc">
          <Card className="report-page-card report-page-card--standard" sx={{ width: '100%', maxWidth: '8.5in' }}>
            <CardContent>
              <TableOfContents items={report.tocItems} />
            </CardContent>
          </Card>
        </Box>

        <Box className="pdf-page exec-page" sx={{ display: 'flex', justifyContent: 'center' }} aria-label="pdf-page-3" data-report-section="executive">
          <Card className="report-page-card report-page-card--standard exec-wrapper allow-split" sx={{ width: '100%', maxWidth: '8.5in' }}>
            <CardContent id="executive-security-assessment" className="exec-embed allow-split">
              <ExecutiveReportAPIPentest minimal={true} reportData={report} primary={primaryColor} company={companyName} client={report.clientName} />
            </CardContent>
          </Card>
        </Box>

        <Box className="pdf-page" sx={{ display: 'flex', justifyContent: 'center' }} aria-label="pdf-page-4" data-report-section="methodology">
          <Card className="report-page-card report-page-card--standard" sx={{ width: '100%', maxWidth: '8.5in' }}>
            <MethodologySection primary={primaryColor} />
          </Card>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Typography className="report-section-heading" variant="h4" id="findings" sx={{ my: 3, textAlign: 'center' }}>
            Findings
          </Typography>
        </Box>

        {report.findings.map((finding, index) => (
          <Box key={finding.id} className="pdf-page" sx={{ display: 'flex', justifyContent: 'center' }} aria-label={`pdf-page-${index + 5}`} data-report-section="finding">
            <Card className="report-page-card report-page-card--standard finding-card" sx={{ width: '100%', maxWidth: '8.5in', mb: 2 }}>
              <CardContent className="finding-card__content">
                <Box className="finding-header">
                  <Typography id={finding.id} variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {`${index + 1}. ${finding.title}`}
                  </Typography>
                  <SeverityChip label={finding.severity || 'Medium'} />
                  {finding.asset ? (
                    <Typography variant="body1" gutterBottom>
                      <strong>Endpoint:</strong> {finding.asset}
                    </Typography>
                  ) : null}
                  <Divider sx={{ my: 2 }} />
                </Box>

                <Box className="finding-markdown">
                  <ReactMarkdown className="markdown-content" remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSlug]}>
                    {finding.cleanedDetails || 'No additional details provided.'}
                  </ReactMarkdown>
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>
    </>
  );
};

export default ReportsPageAPIPentest;
